import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { loadRuntimeConfig } from '../config/runtime';
import { useSpatialStore } from '../stores/spatialStore';
import type {
  CameraState,
  DrawResult,
  EngineId,
  FeatureCollection,
  InteractionMode,
  LayerManifest,
  PerformanceMetrics,
  RendererMountOptions,
  Selection,
  TimeContext,
  WorldRenderer,
} from './core/types';
import { DEFAULT_CAMERA } from './core/types';
import { createRenderer } from './engineFactory';

export type WorldViewportHandle = {
  getRenderer: () => WorldRenderer | undefined;
  getEngine: () => EngineId | undefined;
  switchEngine: (engine: EngineId) => void;
  setTool: (mode: InteractionMode) => void;
  flyTo: WorldRenderer['flyTo'];
  zoomBy: (factor: number) => void;
  resetCamera: () => void;
  setAutoRotate: (enabled: boolean) => void;
  captureSnapshot: () => Promise<Blob>;
  exportVisibleFeatures: () => FeatureCollection;
  clearTransient: () => void;
  setSelection: (selection: Selection) => void;
  clearSelection: () => void;
  retryLayer: (layerId: string) => Promise<void>;
};

export type WorldViewportProps = {
  layers: LayerManifest[];
  initialCamera?: CameraState;
  engine?: EngineId;
  time?: TimeContext;
  className?: string;
  children?: ReactNode;
  onSelection?: (selection: Selection) => void;
  onDrawCompleted?: (result: DrawResult) => void;
  onMeasureUpdated?: (result: DrawResult) => void;
  onReady?: (engine: EngineId) => void;
  onEngineFallback?: (from: EngineId, to: EngineId, error: Error) => void;
  onLayerLoaded?: (layerId: string) => void;
  onLayerError?: (layerId: string, error: Error) => void;
  onError?: (error: Error) => void;
};

export const WorldViewport = forwardRef<WorldViewportHandle, WorldViewportProps>(function WorldViewport(
  {
    layers,
    initialCamera = DEFAULT_CAMERA,
    engine,
    time,
    className = '',
    children,
    onSelection,
    onDrawCompleted,
    onMeasureUpdated,
    onReady,
    onEngineFallback,
    onLayerLoaded,
    onLayerError,
    onError,
  },
  forwardedRef,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<WorldRenderer | undefined>(undefined);
  const layerStateRef = useRef(new Map<string, LayerManifest>());
  const excludedRuntimeEnginesRef = useRef(new Set<EngineId>());
  const pendingRuntimeFallbackRef = useRef<{ from: EngineId; error: Error } | undefined>(undefined);
  const previousEnginePropRef = useRef<EngineId | undefined>(engine);
  const handoffCameraRef = useRef<CameraState | undefined>(undefined);
  const selectionRef = useRef<Selection>({ type: 'none' });
  const interactionModeRef = useRef<InteractionMode>('select');
  const autoRotateRef = useRef(false);
  const [requestedEngine, setRequestedEngine] = useState<EngineId | undefined>(engine);
  const [activeEngine, setActiveEngine] = useState<EngineId>();
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>();
  const [metrics, setMetrics] = useState<PerformanceMetrics>();
  const latestPropsRef = useRef({
    layers,
    time,
    onSelection,
    onDrawCompleted,
    onMeasureUpdated,
    onReady,
    onEngineFallback,
    onLayerLoaded,
    onLayerError,
    onError,
  });
  latestPropsRef.current = {
    layers,
    time,
    onSelection,
    onDrawCompleted,
    onMeasureUpdated,
    onReady,
    onEngineFallback,
    onLayerLoaded,
    onLayerError,
    onError,
  };

  const setEngineStore = useSpatialStore((state) => state.setEngine);
  const setCameraStore = useSpatialStore((state) => state.setCamera);
  const setSelectionStore = useSpatialStore((state) => state.setSelection);
  const setInteractionModeStore = useSpatialStore((state) => state.setInteractionMode);
  const setTimeStore = useSpatialStore((state) => state.setTime);
  const registerLayers = useSpatialStore((state) => state.registerLayers);
  const setRendererError = useSpatialStore((state) => state.setRendererError);

  const stableInitialCamera = useMemo(() => ({ ...initialCamera }), [
    initialCamera.longitude,
    initialCamera.latitude,
    initialCamera.height,
    initialCamera.heading,
    initialCamera.pitch,
    initialCamera.roll,
  ]);

  useEffect(() => {
    if (previousEnginePropRef.current === engine) return;
    previousEnginePropRef.current = engine;
    handoffCameraRef.current = safeGetCamera(rendererRef.current);
    excludedRuntimeEnginesRef.current.clear();
    pendingRuntimeFallbackRef.current = undefined;
    setRequestedEngine(engine);
  }, [engine]);

  useEffect(() => {
    registerLayers(layers);
  }, [layers, registerLayers]);

  useEffect(() => {
    let cancelled = false;
    let localRenderer: WorldRenderer | undefined;
    const unsubscribe: Array<() => void> = [];
    const container = containerRef.current;
    if (!container) return undefined;

    setStatus('loading');
    setErrorMessage(undefined);
    setRendererError(undefined);
    container.replaceChildren();

    const boot = async () => {
      const config = await loadRuntimeConfig();
      const preferred = requestedEngine ?? config.map.engine;
      const candidates = uniqueEngines([preferred, ...config.map.fallbackEngines, 'native'])
        .filter((candidate) => !excludedRuntimeEnginesRef.current.has(candidate));
      let previousEngine: EngineId | undefined;
      let previousError: Error | undefined;

      for (const candidate of candidates) {
        if (cancelled) return;
        try {
          const renderer = await createRenderer(candidate);
          localRenderer = renderer;
          rendererRef.current = renderer;
          const mountOptions: RendererMountOptions = {
            initialCamera: handoffCameraRef.current ?? stableInitialCamera,
            resetCamera: stableInitialCamera,
            time: latestPropsRef.current.time,
            backgroundColor: '#01050d',
            baseTileUrl: config.map.baseTileUrl,
            baseTileAttribution: config.map.baseTileAttribution,
            cesiumIonToken: config.map.cesiumIonToken || import.meta.env.VITE_CESIUM_ION_TOKEN,
            enableTerrain: config.map.enableTerrain,
            enableBuildings: config.map.enableBuildings,
            reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
              || document.documentElement.dataset.reducedMotion === 'true',
          };
          await renderer.mount(container, mountOptions);
          if (cancelled) {
            renderer.destroy();
            return;
          }

          unsubscribe.push(
            renderer.on('cameraChanged', (camera) => setCameraStore(camera)),
            renderer.on('selectionChanged', (selection) => {
              selectionRef.current = selection;
              setSelectionStore(selection);
              latestPropsRef.current.onSelection?.(selection);
            }),
            renderer.on('drawCompleted', (result) => latestPropsRef.current.onDrawCompleted?.(result)),
            renderer.on('measureUpdated', (result) => latestPropsRef.current.onMeasureUpdated?.(result)),
            renderer.on('layerLoaded', ({ layerId }) => latestPropsRef.current.onLayerLoaded?.(layerId)),
            renderer.on('layerFailed', ({ layerId, error }) => {
              latestPropsRef.current.onLayerError?.(layerId, error);
              const scoped = new Error(`Layer ${layerId} failed: ${error.message}`);
              setRendererError(scoped.message);
              latestPropsRef.current.onError?.(scoped);
            }),
            renderer.on('error', ({ error, recoverable }) => {
              setRendererError(error.message);
              latestPropsRef.current.onError?.(error);
              if (!recoverable && !excludedRuntimeEnginesRef.current.has(candidate)) {
                excludedRuntimeEnginesRef.current.add(candidate);
                const next = candidates.find((engineId) => engineId !== candidate && !excludedRuntimeEnginesRef.current.has(engineId));
                if (next) {
                  handoffCameraRef.current = safeGetCamera(renderer);
                  pendingRuntimeFallbackRef.current = { from: candidate, error };
                  setRequestedEngine(next);
                } else {
                  pendingRuntimeFallbackRef.current = undefined;
                  setStatus('failed');
                  setErrorMessage(`All configured map engines failed. Last error: ${error.message}`);
                  queueMicrotask(() => {
                    if (rendererRef.current === renderer) rendererRef.current = undefined;
                    renderer.destroy();
                  });
                }
              }
            }),
            renderer.on('performanceDegraded', (performance) => setMetrics(performance)),
          );

          layerStateRef.current.clear();
          const initialLayers = latestPropsRef.current.layers;
          for (const layer of initialLayers) {
            try {
              await renderer.addLayer(layer);
              layerStateRef.current.set(layer.id, layer);
            } catch (layerError) {
              const normalized = layerError instanceof Error ? layerError : new Error(String(layerError));
              const scoped = new Error(`Layer ${layer.id} failed: ${normalized.message}`);
              setRendererError(scoped.message);
              latestPropsRef.current.onLayerError?.(layer.id, normalized);
              latestPropsRef.current.onError?.(scoped);
            }
          }

          renderer.setInteractionMode(interactionModeRef.current);
          renderer.setAutoRotate(autoRotateRef.current);
          renderer.highlight(selectionRef.current);
          setCameraStore(renderer.getCamera());
          handoffCameraRef.current = undefined;
          if (latestPropsRef.current.time) setTimeStore(latestPropsRef.current.time);
          setActiveEngine(candidate);
          setEngineStore(candidate);
          setStatus('ready');
          setMetrics(renderer.getPerformanceMetrics());
          latestPropsRef.current.onReady?.(candidate);
          const runtimeFallback = pendingRuntimeFallbackRef.current;
          if (runtimeFallback) {
            pendingRuntimeFallbackRef.current = undefined;
            latestPropsRef.current.onEngineFallback?.(runtimeFallback.from, candidate, runtimeFallback.error);
          } else if (previousEngine && previousError) {
            latestPropsRef.current.onEngineFallback?.(previousEngine, candidate, previousError);
          }
          return;
        } catch (error) {
          const normalized = error instanceof Error ? error : new Error(String(error));
          previousEngine = candidate;
          previousError = normalized;
          handoffCameraRef.current ??= safeGetCamera(localRenderer);
          localRenderer?.destroy();
          localRenderer = undefined;
          rendererRef.current = undefined;
          container.replaceChildren();
        }
      }

      const error = previousError ?? new Error('No map renderer could be initialized.');
      if (!cancelled) {
        setStatus('failed');
        setErrorMessage(error.message);
        setRendererError(error.message);
        latestPropsRef.current.onError?.(error);
      }
    };

    void boot();
    const metricsTimer = window.setInterval(() => {
      const renderer = rendererRef.current;
      if (renderer) setMetrics(renderer.getPerformanceMetrics());
    }, 2_000);

    return () => {
      cancelled = true;
      window.clearInterval(metricsTimer);
      unsubscribe.forEach((remove) => remove());
      localRenderer?.destroy();
      if (rendererRef.current === localRenderer) rendererRef.current = undefined;
    };
  }, [requestedEngine, stableInitialCamera, setCameraStore, setEngineStore, setRendererError, setSelectionStore, setTimeStore]);

  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer || status !== 'ready') return;
    let cancelled = false;
    const pending: Promise<unknown>[] = [];
    const currentIds = new Set(layers.map((layer) => layer.id));
    for (const knownId of layerStateRef.current.keys()) {
      if (!currentIds.has(knownId)) {
        renderer.removeLayer(knownId);
        layerStateRef.current.delete(knownId);
      }
    }
    for (const layer of layers) {
      const previous = layerStateRef.current.get(layer.id);
      if (!previous) {
        pending.push(renderer.addLayer(layer)
          .then(() => layerStateRef.current.set(layer.id, layer))
          .catch((layerError) => reportLayerError(layer.id, layerError, setRendererError, latestPropsRef.current.onLayerError, latestPropsRef.current.onError)));
        continue;
      }

      if (requiresDataReload(previous, layer)) {
        pending.push(renderer.updateLayer(layer)
          .then(() => layerStateRef.current.set(layer.id, layer))
          .catch((layerError) => reportLayerError(layer.id, layerError, setRendererError, latestPropsRef.current.onLayerError, latestPropsRef.current.onError)));
        continue;
      }

      if ((previous.visible ?? true) !== (layer.visible ?? true)) {
        renderer.setLayerVisibility(layer.id, layer.visible ?? true);
      }
      if ((previous.opacity ?? 1) !== (layer.opacity ?? 1)) {
        renderer.setLayerOpacity(layer.id, layer.opacity ?? 1);
      }
      if (JSON.stringify(previous.style ?? {}) !== JSON.stringify(layer.style ?? {})) {
        renderer.setLayerStyle(layer.id, layer.style ?? {});
      }
      layerStateRef.current.set(layer.id, layer);
    }

    void Promise.allSettled(pending).then(() => {
      if (cancelled || rendererRef.current !== renderer) return;
      for (let index = layers.length - 1; index >= 0; index -= 1) {
        renderer.reorderLayer(layers[index].id, layers[index + 1]?.id);
      }
      renderer.highlight(selectionRef.current);
    });
    return () => { cancelled = true; };
  }, [layers, setRendererError, status]);

  useEffect(() => {
    if (!time) return;
    rendererRef.current?.setTime(time);
    setTimeStore(time);
  }, [time, setTimeStore]);

  useImperativeHandle(forwardedRef, () => ({
    getRenderer: () => rendererRef.current,
    getEngine: () => activeEngine,
    switchEngine: (nextEngine) => {
      handoffCameraRef.current = safeGetCamera(rendererRef.current);
      excludedRuntimeEnginesRef.current.clear();
      pendingRuntimeFallbackRef.current = undefined;
      setRequestedEngine(nextEngine);
    },
    setTool: (mode) => {
      interactionModeRef.current = mode;
      rendererRef.current?.setInteractionMode(mode);
      setInteractionModeStore(mode);
    },
    flyTo: async (target, options) => rendererRef.current?.flyTo(target, options),
    zoomBy: (factor) => rendererRef.current?.zoomBy(factor),
    resetCamera: () => rendererRef.current?.resetCamera(),
    setAutoRotate: (enabled) => {
      autoRotateRef.current = enabled;
      rendererRef.current?.setAutoRotate(enabled);
    },
    captureSnapshot: async () => {
      const renderer = rendererRef.current;
      if (!renderer) throw new Error('Map renderer is not ready.');
      return renderer.captureSnapshot();
    },
    exportVisibleFeatures: () => rendererRef.current?.exportVisibleFeatures() ?? { type: 'FeatureCollection', features: [] },
    clearTransient: () => rendererRef.current?.clearTransient(),
    setSelection: (selection) => {
      selectionRef.current = selection;
      rendererRef.current?.highlight(selection);
      setSelectionStore(selection);
      latestPropsRef.current.onSelection?.(selection);
    },
    clearSelection: () => {
      const selection: Selection = { type: 'none' };
      selectionRef.current = selection;
      rendererRef.current?.highlight(selection);
      setSelectionStore(selection);
      latestPropsRef.current.onSelection?.(selection);
    },
    retryLayer: async (layerId) => {
      const renderer = rendererRef.current;
      const layer = latestPropsRef.current.layers.find((candidate) => candidate.id === layerId);
      if (!renderer) throw new Error('Map renderer is not ready.');
      if (!layer) throw new Error(`Layer ${layerId} is not registered.`);
      await renderer.updateLayer(layer);
      layerStateRef.current.set(layer.id, layer);
    },
  }), [activeEngine, setInteractionModeStore, setSelectionStore]);

  return (
    <div className={`world-viewport-host ${className}`} data-engine={activeEngine} data-status={status}>
      <div ref={containerRef} className="world-viewport-host__renderer" />
      {status === 'loading' && (
        <div className="world-viewport-state" role="status">
          <span className="world-viewport-state__spinner" />
          <strong>Initializing spatial engine</strong>
          <small>Loading renderer, base map and typed layers…</small>
        </div>
      )}
      {status === 'failed' && (
        <div className="world-viewport-state world-viewport-state--error" role="alert">
          <strong>Map renderer unavailable</strong>
          <small>{errorMessage}</small>
          <button type="button" onClick={() => { excludedRuntimeEnginesRef.current.delete('native'); setRequestedEngine('native'); }}>Start native fallback</button>
        </div>
      )}
      {activeEngine && (
        <div className="world-viewport-engine" title="Active map engine">
          <span>{activeEngine}</span>
          {metrics && <small>{metrics.fps} FPS · {metrics.visibleFeatures.toLocaleString()} features</small>}
        </div>
      )}
      {children}
    </div>
  );
});

function safeGetCamera(renderer?: WorldRenderer): CameraState | undefined {
  if (!renderer) return undefined;
  try {
    return renderer.getCamera();
  } catch {
    return undefined;
  }
}

function uniqueEngines(engines: EngineId[]): EngineId[] {
  return [...new Set(engines)];
}

function requiresDataReload(previous: LayerManifest, next: LayerManifest): boolean {
  return previous.kind !== next.kind
    || previous.url !== next.url
    || previous.data !== next.data
    || previous.attribution !== next.attribution;
}

function reportLayerError(
  layerId: string,
  layerError: unknown,
  setRendererError: (message?: string) => void,
  onLayerError?: (layerId: string, error: Error) => void,
  onError?: (error: Error) => void,
): void {
  const normalized = layerError instanceof Error ? layerError : new Error(String(layerError));
  const scoped = new Error(`Layer ${layerId} failed: ${normalized.message}`);
  setRendererError(scoped.message);
  onLayerError?.(layerId, normalized);
  onError?.(scoped);
}
